import React from 'react';
import { useParams } from 'react-router-dom';

import PublicJobsPage from './PublicJobsPage';
import CompanyJobsPage from './CompanyJobsPage';
import PublicJobPage from './PublicJobPage';
import { getSubdomainFromLocation } from './useSubdomain';

/**
 * PublicEntry
 *
 * Smart top-level component for the public-facing routes (`/`, `/jobs`,
 * `/c/:slug`). It looks at the current hostname and decides whether the
 * request is on:
 *
 *   • A company subdomain (e.g. `acme.yourapp.com`)
 *       → render `<CompanyJobsPage slug={subdomain} />` directly. The
 *         visitor never sees the master listing; they land on Acme's
 *         careers page exactly the way they would on `careers.acme.com`.
 *
 *   • The apex domain (e.g. `yourapp.com` or `localhost:3000`)
 *       → fall back to the existing routing: `/jobs` shows the master
 *         listing; `/c/:slug` keeps working as a path-based fallback so
 *         shared links from before subdomains still resolve.
 *
 * `mode` lets the same component back two routes:
 *   • mode="root"     → master listing fallback (only used on apex domain);
 *                       with a `:jobId` it is that job's standalone page
 *   • mode="company"  → path-based company page (`/c/:slug`)
 */
export default function PublicEntry({ mode = 'root' }) {
  const subdomain = getSubdomainFromLocation();
  const params = useParams();

  // `/jobs/:jobId` is a single job's own page — on the apex and on a tenant
  // subdomain alike. It is the URL shared links, the jobs feed and search
  // engines use, so it renders just that job (with its own title and
  // structured data) rather than a listing with a dialog on top.
  if (mode === 'root' && params.jobId) {
    return <PublicJobPage jobId={params.jobId} />;
  }

  // Subdomain wins. If we're on `acme.yourapp.com`, render Acme's page
  // regardless of which route the React Router matched.
  if (subdomain) {
    return <CompanyJobsPage slug={subdomain} jobIdOverride={params.jobId} />;
  }

  // No subdomain — defer to the route mode.
  if (mode === 'company') {
    // `/c/:slug` path-based fallback for backward compatibility.
    return <CompanyJobsPage />;
  }

  return <PublicJobsPage />;
}
